import React, { useMemo, useState } from "react";
import SEO from "../components/UI/SEO";
import Loader from "../components/UI/Loader";
import BlogGrid from "../components/Blog/BlogGrid";
import BlogSidebar from "../components/Blog/BlogSidebar";
import BlogCategoryList from "../components/Blog/BlogCategoryList";
import useBlogs from "../hooks/useBlogs";

const PAGE_SIZE = 6;

export default function BlogIndex() {
  const {
    filteredBlogs,
    recentBlogs,
    categories,
    filters,
    setFilters,
    loading,
    error,
  } = useBlogs({ page_size: 100 });

  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(filteredBlogs.length / PAGE_SIZE));
  const paginatedBlogs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredBlogs.slice(start, start + PAGE_SIZE);
  }, [filteredBlogs, page]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <SEO
        title="Blog Solusi IoT Cerdas"
        description="Artikel tentang sensor, monitoring air, smart farming, kontroler, dan implementasi IoT."
        type="website"
        canonicalPath={`${window.location.origin}/blog`}
      />

      <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">
              Blog
            </p>
            <h1 className="mt-3 text-4xl font-bold tracking-tight text-gray-900">
              Insight IoT, monitoring, dan implementasi lapangan
            </h1>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Baca artikel pilihan untuk memahami sensor, controller, smart water
              monitoring, dan smart farming secara lebih praktis.
            </p>
          </div>

          <div className="mt-8 rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
            <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_1fr]">
              <input
                type="text"
                value={filters.keyword}
                onChange={(event) => handleFilterChange("keyword", event.target.value)}
                placeholder="Cari artikel, kategori, penulis, atau kata kunci..."
                className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
              />

              <select
                value={filters.featured}
                onChange={(event) => handleFilterChange("featured", event.target.value)}
                className="rounded-2xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-orange-500"
              >
                <option value="all">Semua artikel</option>
                <option value="featured">Artikel unggulan</option>
              </select>

              <select
                value={filters.sort}
                onChange={(event) => handleFilterChange("sort", event.target.value)}
                className="rounded-2xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-orange-500"
              >
                <option value="newest">Terbaru</option>
                <option value="title_asc">Judul A-Z</option>
                <option value="title_desc">Judul Z-A</option>
              </select>
            </div>

            <div className="mt-4">
              <BlogCategoryList
                categories={categories}
                activeCategory={filters.category}
                onSelect={(value) => handleFilterChange("category", value)}
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              Menampilkan <span className="font-semibold">{filteredBlogs.length}</span> artikel
            </p>
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
          </div>

          <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div>
              {loading ? <Loader text="Memuat artikel..." /> : <BlogGrid blogs={paginatedBlogs} />}

              {!loading && totalPages > 1 ? (
                <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setPage(item)}
                      className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                        page === item
                          ? "bg-orange-600 text-white"
                          : "border border-gray-300 bg-white text-gray-700 hover:border-orange-400 hover:text-orange-600"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <BlogSidebar
              categories={categories}
              recentPosts={recentBlogs}
              activeCategory={filters.category}
              onSelectCategory={(value) => handleFilterChange("category", value)}
            />
          </div>
        </div>
      </section>
    </>
  );
}
